import React, { useEffect, useRef, useState } from 'react';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import isequal from 'lodash.isequal';
import {
  MenuItem, Button, Intent, Icon, Divider, InputGroup, Tag
} from '@blueprintjs/core';
import { getClassName } from './utils';
import './multi-select-custom.scss';

export interface MultiselectTypeahead {
  label: string;
  value: string;
  isPlaceholder?: boolean;
  isSelected?: boolean;
}

type Props = {
  name: string,
  dataTestName: string,
  disabled: boolean,
  priority?: boolean,
  className?: string,
  items: MultiselectTypeahead[],
  fieldValue?: MultiselectTypeahead[],
  placeholder?: string,
  onItemSelect: (name: string, value: MultiselectTypeahead[]) => void;
}

const MultiSelectCustom = ({
  items,
  name,
  dataTestName,
  disabled,
  className,
  priority,
  placeholder,
  fieldValue,
  onItemSelect,
}: Props) => {
  const ref = useRef(null);
  const tooltipRef = useRef(null);
  const { t } = useTranslation();
  const [currentFiledValue, setCurrentFiledValue] = useState<MultiselectTypeahead[]>([]);
  const [options, setOptions] = useState<MultiselectTypeahead[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<MultiselectTypeahead[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<MultiselectTypeahead[]>([]);
  const [selectedOptionsMap, setSelectedOptionsMap] = useState<{ [key: string]: boolean }>({});
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [selectedTagData, setSelectedTagData] = useState<{ tags: MultiselectTypeahead[], counterTags: MultiselectTypeahead[], counter: number }>({
    tags: [],
    counterTags: [],
    counter: 0
  });

  useEffect(() => {
    if (!isequal(options, items)) {
      setOptions(items);
      setFilteredOptions(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (isPopoverOpen) {
      return;
    }

    if (!isPopoverOpen && Object.keys(selectedOptionsMap).length) {
      const notSelectedOptions = items.filter((item) => !selectedOptionsMap[item?.value]);
      setFilteredOptions(notSelectedOptions);
      if (!priority) {
        setSelectedOptions(items.filter((item) => selectedOptionsMap[item?.value]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopoverOpen, items]);

  useEffect(() => {
    if (!fieldValue || !Array.isArray(fieldValue)) {
      return;
    }

    const newSelectedItemsMap: { [key: string]: boolean } = (fieldValue || [])
      .reduce((total, actual) => ({
        ...total,
        [actual.value]: true
      }), {});

    if (!priority) {

      setSelectedOptionsMap(newSelectedItemsMap);
      setSelectedTagData(getInputLabelTagsOrdered(fieldValue));
      setSelectedOptions(fieldValue);

      if (fieldValue.length === 0 && options.length > 0) {
        setSelectedOptions([]);
        setFilteredOptions(options);
      }

      if (fieldValue.length > 0) {
        setSelectedOptions(fieldValue);
        const notSelectedOptions = items.filter((item) => !newSelectedItemsMap[item?.value]);
        setFilteredOptions(notSelectedOptions);
      }

      return;
    }

    if (fieldValue?.length === 0 && options.length > 0) {
      setSelectedOptions([]);
      setFilteredOptions(options);
      return;
    }

    setSelectedOptionsMap(newSelectedItemsMap);
    setSelectedTagData(getInputLabelTagsOrdered(fieldValue));
    setSelectedOptions(fieldValue);
    const notSelectedOptions = items.filter((item) => !newSelectedItemsMap[item?.value]);
    setFilteredOptions(notSelectedOptions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue, priority]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !(ref.current as any)?.contains(event.target)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);

    const handleClickOutsideTooltip = (event: any) => {
      if (tooltipRef.current && !(tooltipRef.current as any)?.contains(event.target)) {
        setTooltipOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('click', handleClickOutsideTooltip, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutsideTooltip, true);
    };
  }, []);

  const getTotalNumberOfVisibleTags = (offsetWidth: number): number => {
    const tagWidth = 50;
    const maxTags = 4;
    const tags = Math.floor(offsetWidth) / tagWidth;
    if (Number.isNaN(tags) || tags <= 0) {
      return 1;
    }

    if (tags > maxTags) {
      return maxTags;
    }

    return tags;
  }

  const moveUp = (inx: number) => {
    if (inx > 0) {
      const updated = [
        ...selectedOptions.slice(0, inx - 1),
        selectedOptions[inx],
        selectedOptions[inx - 1],
        ...selectedOptions.slice(inx + 1)
      ]
      setSelectedOptions(updated);
      const updatedMap: { [key: string]: boolean } = updated.reduce((total, actual) => ({
        ...total,
        [actual.value]: true
      }), {});
      setSelectedTagData(getInputLabelTagsOrdered(updated));
      onItemSelect(name, updated);
    }
  }

  const moveDown = (inx: number) => {
    if (inx < selectedOptions.length - 1) {
      const updated = [
        ...selectedOptions.slice(0, inx),
        selectedOptions[inx + 1],
        selectedOptions[inx],
        ...selectedOptions.slice(inx + 2)
      ];
      setSelectedOptions(updated);
      const updatedMap: { [key: string]: boolean } = updated.reduce((total, actual) => ({
        ...total,
        [actual.value]: true
      }), {});
      setSelectedTagData(getInputLabelTagsOrdered(updated));
      onItemSelect(name, updated);

    }
  }

  const getInputLabelTagsOrdered = (selected: MultiselectTypeahead[]) => {
    if (selected?.length === 0) {
      return {
        tags: [],
        counterTags: [],
        counter: 0,
      }
    }

    const offsetWidth = (ref?.current as any)?.offsetWidth - 70;
    const totalTags = getTotalNumberOfVisibleTags(offsetWidth);
    const tags = cloneDeep(selected).splice(0, totalTags);
    const counterTags = cloneDeep(selected).slice(totalTags);

    return {
      tags,
      counterTags,
      counter: counterTags.length
    }
  }

  const filterUndefinedItems = (data: MultiselectTypeahead[]): MultiselectTypeahead[] => {
    return data.filter((item) => item != null);
  }

  const handleOpenPoover = () => {
    setPopoverOpen(!isPopoverOpen);
  }

  const handleChangeSearch = (e: any) => {
    const { value } = e.target;
    setFilteredOptions(options.filter((item) => item?.value?.toLocaleLowerCase().includes(value.toLocaleLowerCase())));
  }

  const handleSelectItem = (item: { value: string, label: string }, isSelectedList: boolean) => () => {
    if (disabled) {
      return;
    }
    const isSelected = selectedOptionsMap[item.value] === true;
    const newSelectedItemsMap = {
      ...selectedOptionsMap,
      [item.value]: !isSelected
    }

    let newSelectedItems = filterUndefinedItems(options).filter((item) => newSelectedItemsMap?.[item.value]);
    setSelectedOptionsMap(newSelectedItemsMap);
    if (isSelectedList) {
      newSelectedItems = selectedOptions.filter((data) => data.value !== item.value);
      setFilteredOptions(items.filter((data) => (!data ? false : !newSelectedItemsMap?.[data.value])));
    } else if (priority) {
      newSelectedItems = [...selectedOptions, item]
      setFilteredOptions(filteredOptions.filter((x) => x.value !== item.value));
    }
    setSelectedOptions(newSelectedItems)
    setSelectedTagData(getInputLabelTagsOrdered(newSelectedItems))
    if (priority) {
      onItemSelect(name, newSelectedItems);
      // setSelectedTagData(getInputLabelTags(cloneDeep(items), newSelectedItemsMap));
    } else {
      onItemSelect(name, newSelectedItems);
    }
    onItemSelect(name, newSelectedItems);
  }

  const handleRemoveTooltipItem = (item: { value: string, label: string }) => (e: any) => {
    if (disabled) {
      return;
    }
    e.stopPropagation && e.stopPropagation();
    setPopoverOpen(false);
    const newSelectedItemsMap = {
      ...selectedOptionsMap,
      [item.value]: false
    }

    setSelectedOptionsMap(newSelectedItemsMap);
    setOptions(filterUndefinedItems(items).filter((item) => !newSelectedItemsMap?.[item.value]));
    const newSelectedItems = filterUndefinedItems(options).filter((item) => newSelectedItemsMap?.[item.value]);
    setSelectedTagData(getInputLabelTagsOrdered(newSelectedItems))
    onItemSelect(name, newSelectedItems);
  }

  const handleRemove = (value: string) => () => {
    if (disabled) {
      return;
    }

    const newSelectedItemsMap = {
      ...selectedOptionsMap,
      [value]: false
    }

    const newSelectedItems = filterUndefinedItems(options).filter((item) => newSelectedItemsMap?.[item.value]);
    const newFilteredOptions = filterUndefinedItems(options).filter((item) => !newSelectedItemsMap?.[item.value]);

    setSelectedOptionsMap(newSelectedItemsMap);
    setSelectedOptions(newSelectedItems)
    setFilteredOptions(newFilteredOptions)
    setSelectedTagData(getInputLabelTagsOrdered(newSelectedItems))
    onItemSelect(name, newSelectedItems);
  }

  const handleOpenTooltip = () => {
    setTooltipOpen(true);
  }

  const handleSelectAll = () => {
    const newSelectedItems = filterUndefinedItems(items);
    const newSelectedItemsMap = newSelectedItems.reduce((total, item) => ({
      ...total,
      [item.value]: true,
    }), {});
    setSelectedOptionsMap(newSelectedItemsMap);
    setSelectedTagData(getInputLabelTagsOrdered(newSelectedItems));
    onItemSelect(name, newSelectedItems);
  }

  const handleClearAll = () => {
    setOptions(items);
    setFilteredOptions(items);
    setSelectedOptions([]);
    setSelectedOptionsMap({});
    setSelectedTagData({
      tags: [],
      counterTags: [],
      counter: 0,
    });
    onItemSelect(name, []);
  }

  const isItemSelected = (data: any) => {
    return selectedOptionsMap?.[data?.value]
  }

  const getCarets = (inx: number) => {
    return (
      <React.Fragment>
        <div style={{ marginBottom: '5px' }}>
          <Icon
            icon="caret-up"
            style={{ cursor: 'pointer', color: (inx === 0) ? '#d3d3d3' : 'black' }}
            onClick={() => moveUp(inx)}
          />
        </div>
        <div style={{ marginTop: '5px' }}>
          <Icon
            icon="caret-down"
            style={{ cursor: 'pointer', color: (inx === selectedOptions.length - 1) ? '#d3d3d3' : 'black' }}
            onClick={() => moveDown(inx)}
          />
        </div>
      </React.Fragment>
    )
  }

  const renderDefaultMultiselectItem = (isSelectedList: boolean, showPriority?: boolean) => (item: { label: string, value: string }, inx: number) => {
    if (!item) return null;
    const { label, value } = item;
    const thelabel = (isSelectedList && showPriority) ? `(Priority ${inx + 1}) ${label}` : label
    return (
      <div style={{ display: 'flex' }} key={value}>
        {isSelectedList && showPriority && getCarets(inx)}
        <div style={{ width: '100%' }}>
          <MenuItem
            active={selectedOptionsMap[value]}
            key={value}
            text={thelabel}
            className={isItemSelected(item) ? 'multi-select-custom-selected' : undefined}
            onClick={handleSelectItem(item, isSelectedList)}
            shouldDismissPopover={false}
          />
          <div />
        </div>
      </div>
    );
  };

  const renderPopover = () => {
    if (!isPopoverOpen || disabled) {
      return null
    }

    return (
      <div className="multi-select-custom-input-popover">
        <header className="multi-select-custom-input-popover-header">
          <div>
            <InputGroup leftIcon="search" onChange={handleChangeSearch} />
          </div>
          <div className="multi-select-custom-input-popover-header-action">
            <Button minimal intent={Intent.PRIMARY} onClick={handleSelectAll}>
              {t('networkTopology.view.selectAll')}
            </Button>
            <Button minimal intent={Intent.PRIMARY} onClick={handleClearAll}>
              {t('networkTopology.view.clear')}
            </Button>
          </div>
        </header>
        <ul className="multi-select-custom-input-popover-list">
          {
            selectedOptions.map(renderDefaultMultiselectItem(true, priority))
          }
        </ul>
        {!!selectedOptions.length && <Divider />}
        <ul className="multi-select-custom-input-popover-list">
          {
            filteredOptions.map(renderDefaultMultiselectItem(false))
          }
        </ul>
      </div>
    );
  }

  const renderTagTooltip = (counterTags: { value: string; label: string; }[]) => {
    if (disabled) {
      return (
        <div className="multi-select-custom-tooltip">
          {counterTags.map((item) => (
            <div key={item.value} className="multi-select-custom-tooltip-tag">
              {item.label}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="multi-select-custom-tooltip">
        {counterTags.map((item) => (
          <div key={item.value} className="multi-select-custom-tooltip-tag">
            {item.label} <Icon icon="cross" onClick={handleRemoveTooltipItem(item)} />
          </div>
        ))}
      </div>
    );
  }

  const renderInputContent = () => {
    if (!selectedOptions.length) {
      return placeholder;
    }

    if (selectedTagData.tags.length === 1 && selectedTagData.counterTags.length === 0) {
      return (
        <Tag onRemove={handleRemove(selectedTagData.tags[0].value)}>
          {selectedTagData.tags[0].label}
        </Tag>
      );
    }

    return (
      <React.Fragment>
        {selectedTagData.tags.map((item) => (
          <Tag
            key={item.value}
            className="multi-select-custom-input-tag"
            onRemove={handleRemove(item.value)}
          >
            {item.label}
          </Tag>
        ))}
        {selectedTagData.counter > 0 && (
          <Tooltip2
            popoverRef={tooltipRef}
            position="top"
            content={renderTagTooltip(selectedTagData.counterTags)}
            isOpen={isTooltipOpen}
          >
            <Tag onMouseEnter={handleOpenTooltip}>
              +{selectedTagData.counter <= 99 ? selectedTagData.counter : 99}
            </Tag>
          </Tooltip2>
        )}
      </React.Fragment>
    );
  }

  return (
    <div ref={ref} className={getClassName('multi-select-custom', disabled)}>
      <div role="button" tabIndex={0} aria-hidden="true" className={getClassName('multi-select-custom-input', disabled)} onClick={handleOpenPoover}>
        <div className="multi-select-custom-input-content">
          {renderInputContent()}
        </div>
        <div className="multi-select-custom-input-caret">
          <Icon icon="caret-down" />
        </div>
      </div>
      {renderPopover()}
    </div>
  );
}

export default React.memo(MultiSelectCustom);
